import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { reviewAPI } from '../services/api';

const TrainingEssaysList = () => {
  const { user, authToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && authToken) {
      loadEssays();
    }
  }, [user, authToken, currentPage, selectedSubject]);

  const loadEssays = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await reviewAPI.getTrainingEssays(
        authToken, 
        currentPage, 
        20, 
        selectedSubject || null
      );
      
      setEssays(response.essays || []);
      setTotalPages(response.totalPages || 1);
      setAvailableSubjects(response.availableSubjects || []);
      
    } catch (error) {
      console.error('Error loading essays:', error);
      setError('Failed to load training essays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleReviewEssay = (essay) => {
    navigate(`/essay/${essay.id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getGradeDisplay = (originalGrade) => {
    try {
      if (typeof originalGrade === 'string' && originalGrade.startsWith('{')) {
        const gradeData = JSON.parse(originalGrade);
        return gradeData.grade || 'N/A';
      }
      return originalGrade || 'N/A';
    } catch (e) {
      return originalGrade || 'N/A';
    }
  };

  const getStatusDisplay = (essay) => {
    if (essay.status === 'completed' && essay.graded) {
      return { text: 'Reviewed', class: 'status-completed' };
    } else if (essay.status === 'completed') {
      return { text: 'Ready for Review', class: 'status-ready' };
    } else {
      return { text: 'Processing', class: 'status-processing' };
    }
  };

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, marginBottom: '5px' }}>Training Essays Review</h1>
          <p style={{ margin: 0, color: '#64748b' }}>
            Review and edit feedback for student training submissions
          </p>
        </div>
        
        {availableSubjects.length > 1 && (
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Subject:</label>
            <select
              value={selectedSubject}
              onChange={handleSubjectChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Subjects</option>
              {availableSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="error">{error}</div>
      )}

      {loading ? (
        <div className="loading">Loading essays...</div>
      ) : essays.length === 0 ? (
        <div className="empty-state">
          <h3>No training essays found</h3>
          <p>There are no training essays available for review at this time.</p>
        </div>
      ) : (
        <>
          <div className="essay-grid">
            {essays.map(essay => {
              const status = getStatusDisplay(essay);
              return (
                <div key={essay.id} className="essay-card">
                  <div className="essay-meta">
                    <span className="subject-tag">{essay.subject}</span>
                    <span className="grade-tag">{getGradeDisplay(essay.original_grade)}</span>
                  </div>
                  
                  <div className="student-info">
                    <strong>Student #{essay.id}</strong> • {essay.grading_category || essay.category}
                  </div>
                  
                  <div className="essay-excerpt">
                    {essay.cleaned_text ? 
                      essay.cleaned_text.substring(0, 150) + (essay.cleaned_text.length > 150 ? '...' : '') :
                      'No text available'
                    }
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: '#64748b' }}>
                      {formatDate(essay.submitted_at)}
                    </span>
                    <span className={status.class} style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      backgroundColor: status.class === 'status-completed' ? '#dcfce7' : 
                                     status.class === 'status-ready' ? '#dbeafe' : '#fef3c7',
                      color: status.class === 'status-completed' ? '#166534' : 
                             status.class === 'status-ready' ? '#1e40af' : '#92400e'
                    }}>
                      {status.text}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handleReviewEssay(essay)}
                    className="review-button"
                    disabled={essay.status !== 'completed'}
                  >
                    {essay.graded ? 'Review Feedback' : 'Grade & Review'}
                  </button>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <span className="current-page">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrainingEssaysList; 